// ABOUTME: Utility functions for the Zapier facade
// ABOUTME: Includes param preparation, dict normalization, and controlled app checks
import { ControlledAppsResponse } from "./actions";

const GUESS_SENTINEL = "$$$GUESS$$$";

/**
 * The action key for API Request (Beta) actions across all Zapier apps.
 */
export const API_REQUEST_BETA_ACTION_KEY = "_zap_raw_request";

/**
 * Converts key-value entries to Zapier's pipe-separated strdict format.
 */
function toPipeFormat(entries: [string, string][]): string {
  return entries.map(([k, v]) => `${k}|${v}`).join("\n");
}

/**
 * Normalizes parameters for API Request (Beta) actions.
 *
 * Converts querystring objects to pipe-separated format and extracts query
 * params from URLs when the querystring field is empty.
 */
function normalizeApiRequestBetaParams(
  params: Record<string, unknown>,
): Record<string, unknown> {
  const result = { ...params };

  // Normalize querystring to pipe format if it's an object
  const qs = result.querystring;
  if (qs && typeof qs === "object") {
    result.querystring = toPipeFormat(
      Object.entries(qs).map(([k, v]) => [k, String(v)]),
    );
  }

  // Extract query params from URL if querystring is empty
  const { url, querystring } = result;
  if (typeof url === "string" && !querystring) {
    try {
      const urlObj = new URL(url);
      const entries = [...urlObj.searchParams.entries()];
      if (entries.length > 0) {
        urlObj.search = "";
        result.url = urlObj.toString();
        result.querystring = toPipeFormat(entries);
      }
    } catch {
      // swallow
    }
  }

  return result;
}

/**
 * Prepare params for sending to the monolith.
 *
 * - Removes guess sentinel values
 * - For API Request (Beta) actions, normalizes strdict fields and extracts
 *   query params from URLs
 */
export const prepareParamsForSend = (
  params: Record<string, unknown>,
  action?: string,
): Record<string, unknown> => {
  let processedParams = params;

  if (action === API_REQUEST_BETA_ACTION_KEY) {
    processedParams = normalizeApiRequestBetaParams(params);
  }

  const outParams: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(processedParams)) {
    if (v !== GUESS_SENTINEL) {
      outParams[k] = v;
    }
  }
  return outParams;
};

/** Check if an object is a dictionary */
const isDict = (obj: unknown): obj is Record<string, unknown> => {
  return obj !== null && typeof obj === "object" && !Array.isArray(obj);
};

/** Check if an object is a list */
const isList = (obj: unknown): obj is unknown[] => {
  return Array.isArray(obj);
};

/**
 * Flatten a dictionary and list into a single level of keys
 *
 * i.e.
 * {
 *   "a": {
 *     "b": 1,
 *     "c": 2,
 *   },
 * }
 *
 * Becomes:
 * {
 *   "a__b": 1,
 *   "a__c": 2,
 * }
 */
export const complexFlatten = (
  dict: unknown,
  dictSplitter = "__",
  listSplitter = "[]",
): Record<string, unknown> => {
  if (!isDict(dict)) return {};

  // Flatten nested dictionaries
  const flattenDicts = (
    obj: Record<string, unknown>,
    parentKey = "",
  ): Record<string, unknown> => {
    const items: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      const newKey = parentKey ? `${parentKey}${dictSplitter}${key}` : key;

      if (isDict(value)) {
        Object.assign(items, flattenDicts(value, newKey));
      } else {
        items[newKey] = value;
      }
    }

    return items;
  };

  // Flatten nested lists
  const flattenLists = (
    obj: Record<string, unknown>,
  ): Record<string, unknown> => {
    const items: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      items[key] = value;

      if (isList(value) && value.some(isDict)) {
        const dicts = value.filter(isDict).map((d) => complexFlatten(d));
        const keys = new Set(
          dicts.flatMap((d: Record<string, unknown>) => Object.keys(d)),
        );

        for (const thisKey of keys) {
          const newKey = `${key}${listSplitter}${thisKey}`;
          items[newKey] = dicts.map((d) => {
            const val = d[thisKey as keyof typeof d];
            return val !== undefined ? complexFlatten(val) : null;
          });
        }
      }
    }

    return items;
  };

  const flattened = flattenDicts(dict);
  return flattenLists(flattened);
};

/**
 * Gets the versionless API name by removing the version suffix.
 * For example: "GoogleMailV2CLIAPI@2.0.2" -> "GoogleMailV2CLIAPI"
 *
 * @param selectedApi - The API name with version suffix
 * @returns The versionless API name
 */
export function getVersionlessApi(selectedApi: string): string {
  if (!selectedApi) return "";

  return selectedApi.split("@")[0];
}

/**
 * Check if the app is controlled and should be filtered out.
 *
 * There are two strategies:
 * - allowlist: All apps are filtered out by default, unless they are explicitly allowed
 * - denylist: Filter out any apps that are denied
 *
 * @param versionlessSelectedApi App to check for
 * @param param1 Result of `getControlledApps` call
 * @returns True if the app is controlled and should be filtered out, false otherwise
 */
export const isVersionlessSelectedApiControlled = (
  versionlessSelectedApi: string,
  { apps, strategy }: ControlledAppsResponse,
): boolean => {
  if (strategy.strategy === "allowlist") {
    // for the allowlist strategy, ALL apps are filtered out by default
    // unless they are explicitly allowed
    return !apps.some(
      (app) =>
        app.selected_api?.split("@")[0] === versionlessSelectedApi &&
        app.status === "allowed",
    );
  }

  if (strategy.strategy === "denylist") {
    // for the denylist strategy, filter out any apps that are denied
    return apps.some(
      (app) =>
        app.selected_api?.split("@")[0] === versionlessSelectedApi &&
        app.status === "denied",
    );
  }

  return false;
};

/**
 * Format parent_key for label display
 *
 * @param parentKey - The parent key to format
 * @returns The formatted parent key
 */
export function formatParentKeyForLabel(parentKey: string): string {
  // Remove double underscores and trailing (number) if present
  // Example: Website__(7) -> Website
  return parentKey.replace(/__\(\d+\)$/, "").replace(/__/g, " ");
}
